import React, { useState, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import config from './config/environment';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import ErrorBoundary from './components/ErrorBoundary';
import { tabFromHash } from './components/navigation';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

// Single source of truth for storage keys. Previously App.tsx hard-coded
// 'admin_access_token' / 'admin_refresh_token' while api.ts wrote via
// config.STORAGE_KEYS.AUTH_TOKEN (= 'vozyme_admin_token'). Mismatch meant
// every hard refresh threw the user back to the login screen because the
// auth check looked at the wrong key.
const AUTH_TOKEN_KEY = config.STORAGE_KEYS.AUTH_TOKEN;
const REFRESH_TOKEN_KEY = config.STORAGE_KEYS.REFRESH_TOKEN;

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // The page lives in the URL hash (#/rides), so a refresh or a shared link
  // opens the same page. A hash also needs no nginx rewrite on the admin host.
  const [currentTab, setCurrentTab] = useState<string>(() => tabFromHash(window.location.hash));

  useEffect(() => {
    const onHashChange = (): void => setCurrentTab(tabFromHash(window.location.hash));
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const handleTabChange = (tab: string): void => {
    if (tabFromHash(`#/${tab}`) !== tab) return;
    window.location.hash = `/${tab}`;
    setCurrentTab(tab);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      try {
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (token) {
          // Don't verify token on startup, just check if it exists
          setIsAuthenticated(true);
        }
      } catch (error) {
        // Token is invalid or expired
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Listen for storage changes (like token removal)
  useEffect(() => {
    const handleStorageChange = (): void => {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      setIsAuthenticated(!!token);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleLogin = (): void => {
    setIsAuthenticated(true);
  };

  const handleLogout = (): void => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    setIsAuthenticated(false);
  };

  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="loading-container">
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
          <p>Зареждане на VozyMe Admin...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="app">
        {isAuthenticated ? (
          <Dashboard
            onLogout={handleLogout}
            currentTab={currentTab}
            onTabChange={handleTabChange}
          />
        ) : (
          <Login onLogin={handleLogin} />
        )}
        <ToastContainer
          position="top-right"
          autoClose={4000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    </ErrorBoundary>
  );
};

export default App;
